/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the team screen draws, worked out without drawing it.
 *
 * The screen is a card per plan, and every row in a card — somebody seated,
 * an invitation still open, a seat nobody sits in — has to land under the
 * plan that pays for it. That grouping, the search, and the three states a
 * meter can be in are decisions; the components only render them.
 */

import type {
  TeamOverview, TeamPendingInvite, TeamPerson, TeamQuota, TeamSeatBlock,
} from '@/services/requests/housien-services/AgenciesService.team';
import { type PlanTier, resolvePlanTier } from '@/shared/ui/components/basic/icons/PlanIcons';
import {
  type NameablePlan, planEntryFor,
} from '@/features/onboarding/components/sections/InviteTeam.rows';

/** A meter's state. Attention at 90%, and its own tone once over. */
export type MeterTone = 'normal' | 'warning' | 'over';

/** A row in a plan's card: somebody seated, somebody invited, or nobody. */
export type TeamRow =
  | { kind: 'person'; person: TeamPerson }
  | { kind: 'pending'; invite: TeamPendingInvite }
  | { kind: 'vacant'; seatId: string | null };

/** One plan's card. */
export interface TeamGroup {
  planId: string;
  bought: number;
  taken: number;
  free: number;
  rows: TeamRow[];
}

const WARNING_AT = 0.9;

const TIER_RANK: Record<PlanTier, number> = { emerald: 0, ruby: 1, diamond: 2 };

/**
 * How high a plan sits among the tiers.
 *
 * The catalogue is asked first. A subscription line often carries the
 * catalogue's UUID rather than a slug, and a UUID says nothing about a tier,
 * so reading the id alone would rank every card as the entry tier and leave
 * the order to chance. The id is only the last resort, for a plan the
 * catalogue has not loaded or no longer sells.
 *
 * @param planId - The spelling the block carries.
 * @param catalogue - The plans the app knows about.
 * @returns 0 for the entry tier, 2 for the top one.
 */
export const tierRankOf = (
  planId: string, catalogue: NameablePlan[] = [],
): number => {
  const entry = planEntryFor(catalogue, planId);
  return TIER_RANK[resolvePlanTier(entry?.key || entry?.value || planId)];
};

/** How full a meter is, from 0 to 1, or null when there is no ceiling. */
export const meterFill = (quota: TeamQuota | undefined): number | null => {
  if (!quota || quota.total == null || quota.total <= 0) return null;
  return Math.min(1, quota.used / quota.total);
};

/**
 * Which of the three states a meter is in.
 *
 * A quota with no ceiling is never in trouble — there is nothing to be close
 * to — so it reads as normal rather than as full.
 */
export const meterTone = (quota: TeamQuota | undefined): MeterTone => {
  const fill = meterFill(quota);
  if (fill == null) return 'normal';
  if (fill >= 1) return 'over';
  return fill >= WARNING_AT ? 'warning' : 'normal';
};

/** The same three states, for the agency's one shared drive. */
export const driveTone = (
  usedBytes: number | null, totalGb: number | null,
): MeterTone => meterTone(
  usedBytes == null || totalGb == null
    ? undefined
    : { used: usedBytes, total: totalGb * 1024 ** 3 },
);

/**
 * Matches.
 */
const matches = (haystack: string, needle: string): boolean => (
  haystack.toLowerCase().includes(needle)
);

/** Whether a person answers to what was typed in the search box. */
export const personMatches = (person: TeamPerson, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const name = `${person.first_name ?? ''} ${person.last_name ?? ''}`;
  return matches(name, needle) || matches(person.email ?? '', needle);
};

/** The same, for an invitation: it has an address and nothing else yet. */
export const inviteMatches = (invite: TeamPendingInvite, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  return !needle || matches(invite.email ?? '', needle);
};

/**
 * The cards the screen draws, in the order the subscription bought them.
 *
 * A search hides the empty seats: they answer to no name, and leaving them in
 * would make a filtered card look like it still had room for the person being
 * looked for.
 *
 * Anyone whose plan nothing pays for — every seat written before the plan was
 * carried — lands in the first card rather than off the screen. A person the
 * agency cannot see is a person it cannot remove.
 *
 * The cards come back best tier first, so the plan that costs the most heads
 * the screen. That is presentation only: the card those unpaid-for people
 * land in stays the subscription's own first block, which is also the block
 * the server charged their seats to. Ordering the blocks themselves would
 * move the people without moving the charge, and the person and the chair
 * would sit in different cards.
 */
export const teamGroups = (
  overview: TeamOverview | null, search = '', catalogue: NameablePlan[] = [],
): TeamGroup[] => {
  const blocks: TeamSeatBlock[] = overview?.seats.by_plan ?? [];
  if (!blocks.length) return [];
  const known = new Set(blocks.map((block) => block.plan_id));
  const fallback = blocks[0].plan_id;
  const planOf = (id: string | null) => (id && known.has(id) ? id : fallback);
  const searching = Boolean(search.trim());

  return byTierDescending(blocks.map((block) => ({
    planId: block.plan_id,
    bought: block.bought,
    taken: block.taken,
    free: block.free,
    rows: [
      ...(overview?.people ?? [])
        .filter((person) => planOf(person.plan_id) === block.plan_id)
        .filter((person) => personMatches(person, search))
        .map((person) => ({ kind: 'person' as const, person })),
      ...(overview?.pending ?? [])
        .filter((invite) => planOf(invite.plan_id) === block.plan_id)
        .filter((invite) => inviteMatches(invite, search))
        .map((invite) => ({ kind: 'pending' as const, invite })),
      ...(searching ? [] : freeSeatRows(overview, block, planOf)),
    ],
  })), catalogue);
};

/**
 * The same cards, best tier first.
 *
 * Sorting is stable, so two cards on the same tier keep the order the
 * subscription bought them in — an agency with two Rubi lines should not see
 * them swap places between loads.
 *
 * @param groups - The cards, in subscription order.
 * @param catalogue - The plans the app knows about, for the tiers.
 * @returns The cards, highest tier first.
 */
const byTierDescending = (
  groups: TeamGroup[], catalogue: NameablePlan[],
): TeamGroup[] => [...groups].sort((left, right) => (
  tierRankOf(right.planId, catalogue) - tierRankOf(left.planId, catalogue)
));

/** Free seats left standing when a card is folded. */
export const FREE_SEATS_SHOWN = 3;

/** How many free seats a card needs before folding is worth a click. */
export const FREE_SEATS_FOLD_FROM = 5;

/** A card's rows, with the free seats past the fold counted rather than drawn. */
export interface FoldedRows {
  rows: TeamRow[];
  hidden: number;
  foldable: boolean;
}

/**
 * The rows a card draws, with its spare seats folded away.
 *
 * A Diamante agency buys forty seats and fills three, and forty identical
 * "free" rows push the next plan off the screen. A few are kept so the card
 * still reads as having room; the rest are counted behind one line.
 *
 * Folding starts at five because below that the button saves a row or two
 * and costs a click. The free seats are always the tail of the card, so the
 * fold is a cut and never a filter.
 *
 * @param rows - The card's rows, people first and free seats last.
 * @param expanded - Whether the reader asked to see them all.
 * @returns The rows to draw, how many are behind the fold, and whether
 *   there is anything to fold at all.
 */
export const foldFreeSeats = (rows: TeamRow[], expanded: boolean): FoldedRows => {
  const vacant = rows.filter((row) => row.kind === 'vacant').length;
  const foldable = vacant >= FREE_SEATS_FOLD_FROM;
  if (!foldable || expanded) return { rows, hidden: 0, foldable };
  return {
    rows: rows.slice(0, rows.length - vacant + FREE_SEATS_SHOWN),
    hidden: vacant - FREE_SEATS_SHOWN,
    foldable,
  };
};

/**
 * The free seats of one block, as rows.
 *
 * A seat somebody left keeps its own row and its id. A seat that was bought
 * and never filled has no row anywhere — nobody ever sat in it — so it is
 * drawn from the block's own count. The card header counts those seats
 * either way, and a card that drew no row for them would promise a seat with
 * no way to use it.
 */
const freeSeatRows = (
  overview: TeamOverview | null,
  block: TeamSeatBlock,
  planOf: (id: string | null) => string,
): TeamRow[] => {
  const known = (overview?.vacant ?? [])
    .filter((seat) => planOf(seat.plan_id) === block.plan_id)
    .slice(0, Math.max(0, block.free))
    .map((seat) => ({ kind: 'vacant' as const, seatId: seat.seat_id }));
  const unprovisioned = Math.max(0, block.free - known.length);
  return [
    ...known,
    ...Array.from({ length: unprovisioned }, () => ({
      kind: 'vacant' as const, seatId: null,
    })),
  ];
};

/**
 * The plans somebody can be moved into, from where they sit now.
 *
 * Their own plan is offered and marked rather than hidden: a menu that drops
 * the current one makes the reader work out where they are by elimination.
 */
export const moveTargets = (
  blocks: TeamSeatBlock[], currentPlanId: string | null,
): Array<{ planId: string; current: boolean; disabled: boolean }> => (
  blocks.map((block) => ({
    planId: block.plan_id,
    current: block.plan_id === currentPlanId,
    disabled: block.plan_id === currentPlanId || block.free <= 0,
  }))
);

/** The initials an avatar shows, falling back to the address. */
export const initialsOf = (person: {
  first_name?: string; last_name?: string; email?: string;
}): string => {
  const first = (person.first_name || '').trim();
  const last = (person.last_name || '').trim();
  const letters = `${first[0] ?? ''}${last[0] ?? ''}`.trim();
  return (letters || (person.email || '?')[0] || '?').toUpperCase();
};
